import { Info } from 'luxon';

import { SelectOption } from '@ui/components/form/common';
import { Profile } from '@apps/auth/models';

import { Periodicity, IntervalType, ReptConstants } from '../../rept.constants';
import { ReportInterval, Template } from './template';
import { Report } from './report';

export class DetailedReport extends Report
{
    private static FrLang = 'fr-FR';
    private static EnLang = 'en-US';
    public language: string;
    public detailedVisibility: string;
    public detailedPeriodicity: string;
    public detailedHour: string;
    public detailedHours: string;
    public detailedDays: string;
    // public detailedAnalysis: string;
    public detailedTick: string = '';
    public statusKey: string = '';
    public statusClass: string = '';
    public contractualKey: string = '';
    public contractualClass: string = '';
    public visibilityKey: string = '';
    public visibilityClass: string = '';

    public static mapperFromObject (
        source: Report,
        Templates: Template[],
        periodicities: SelectOption[],
        predefIntervals: SelectOption[],
        intervalTypes: SelectOption[],
        profile: Profile
    ): DetailedReport
    {
        const conjonction: string = profile.language === DetailedReport.FrLang ? 'et' : 'and';
        const le: string = profile.language === DetailedReport.FrLang ? 'le' : 'on';
        const les: string = profile.language === DetailedReport.FrLang ? 'les' : 'on';
        const template = Templates.find((br: Template): boolean =>
            br.templateId === source.templateId
        );

        const target = new DetailedReport();

        Object.assign(target, source);

        target.language = profile.language;
        target.cultureName = source.cultureName !== null ? source.cultureName : DetailedReport.FrLang;
        target.detailedPeriodicity = periodicities
            .find((so: SelectOption): boolean =>
                so.value === source.scheduleDescription.periodicity
            ).label
            .replace('PERIODICITY', 'PERIODICITY_FULL');

        target.statusKey = source.active ? 'ACTIVE' : 'INACTIVE';
        target.statusClass = ReptConstants.getCssStatus(source.active);

        target.contractualKey = source.isContractual ? 'YES' : 'NO';
        target.contractualClass = ReptConstants.getCssContractual(source.isContractual);

        target.visibilityKey = template.isPublic ? 'PUBLIC' : 'PRIVATE';
        target.visibilityClass = ReptConstants.getCssVisibility(template.isPublic);

        let weekdays: string[];
        let wds: any;
        switch (source.scheduleDescription.periodicity)
        {
            case Periodicity.DAILY:
                target.detailedHours = source.scheduleDescription.hours.map((h: number): string => `${h.toString()}h`).join(', ');

                break;

            case Periodicity.WEEKLY:
                weekdays = [];
                wds = Info.weekdays('long', { locale: profile.language });

                source.scheduleDescription.weekDays.forEach((wd: number): number => weekdays.push(wds[(wd + 6) % 7]));
                if (weekdays.length === 1)
                {
                    target.detailedDays = `${le} ${weekdays[0]}`;
                }
                else if (weekdays.length === 2)
                {
                    target.detailedDays = `${les} ${weekdays[0]} ${conjonction} ${weekdays[1]}`;
                }
                else
                {
                    target.detailedDays = weekdays.slice(0, weekdays.length - 1).join(', ');
                    target.detailedDays += `${les} ${conjonction} ${weekdays[weekdays.length - 1]}`;
                }

                target.detailedHour = source.scheduleDescription.hour !== null ? `${source.scheduleDescription.hour.toString()}h` : '';

                break;

            case Periodicity.MONTHLY:
                {
                    const monthdays: string[] = source.scheduleDescription.monthDays.map((wd: number): string =>
                        DetailedReport.suffixMonthDay(wd, profile.language)
                    );
                    if (monthdays.length === 1)
                    {
                        target.detailedDays = `${le} ${monthdays[0]}`;
                    }
                    else if (monthdays.length === 2)
                    {
                        target.detailedDays = `${les} ${monthdays[0]} ${conjonction} ${monthdays[1]}`;
                    }
                    else
                    {
                        target.detailedDays = monthdays.slice(0, monthdays.length - 1).join(', ');
                        target.detailedDays += `${les} ${conjonction} ${monthdays[weekdays.length - 1]}`;
                    }

                    target.detailedHour = `${source.scheduleDescription.hour.toString()}h`;
                }

                break;
        }

        if (source.scheduleDescription.intervalType === IntervalType.CUSTOM)
        {
            const reportInterval = template.reportIntervals.find((ri: ReportInterval): boolean =>
                ri.periodicity === source.scheduleDescription.periodicity
            );

            target.detailedTick = reportInterval.customInterval.ticks[source.scheduleDescription.tickIndex];
        }

        // target.detailedInternalRecipients = users.filter((u: User) =>
        //     source.internalRecipients.includes(u.id)
        // );

        return target;
    }

    private static suffixMonthDay (md: number, lang: string): string
    {
        let suffix: string;

        if (lang === DetailedReport.EnLang)
        {
            // https://stackoverflow.com/a/13627586
            suffix = 'th';
            if (md % 10 === 1 && md !== 11)
            {

                suffix = 'st';
            }
            else if (md % 10 === 2 && md !== 12)
            {
                suffix = 'nd';
            }
            else if (md % 10 === 3 && md !== 13)
            {
                suffix = 'rd';
            }
        }
        else
        {
            suffix = md === 1 ? 'er' : '';
        }

        return `${md}${suffix}`;
    }
}
